import { useEffect, useReducer } from 'react';
import { Button, Field, Flex, Input, Switch, Text, Textarea } from '@optiaxiom/react';
import { IconXmark } from '@optimizely/axiom-icons';

import { Sidebar } from '@frontend/components/sidebar/Sidebar';
import { UnifiedAgent } from '@frontend/types/agent';

import { useAgentDirectoryDetails } from '../hooks/useAgentDirectory';
import { AgentDirectoryDetails } from '../services/agent-directory';

// Form state type based on editable fields from AgentDirectoryDetails
type PublishFormState = {
  is_featured: boolean;
  is_hidden: boolean;
  details_url: string;
  release_notes: string;
  etag: string;
  tagsInput: string; // Keep as string for UI convenience
};

// Reducer action types
type FormAction =
  | { type: 'HYDRATE_FORM'; payload: AgentDirectoryDetails }
  | { type: 'UPDATE_FIELD'; field: keyof PublishFormState; value: string | boolean }
  | { type: 'RESET_FORM' };

// Initial form state
const initialFormState: PublishFormState = {
  is_featured: false,
  is_hidden: false,
  details_url: '',
  release_notes: '',
  etag: '',
  tagsInput: '',
};

// Form reducer function
function formReducer(state: PublishFormState, action: FormAction): PublishFormState {
  switch (action.type) {
    case 'HYDRATE_FORM':
      return {
        is_featured: action.payload.is_featured,
        is_hidden: action.payload.is_hidden,
        details_url: action.payload.details_url || '',
        release_notes: action.payload.release_notes || '',
        etag: action.payload.etag || '',
        tagsInput: action.payload.tags?.map(tag => tag.name).join(', ') || '',
      };
    case 'UPDATE_FIELD':
      return {
        ...state,
        [action.field]: action.value,
      };
    case 'RESET_FORM':
      return initialFormState;
    default:
      return state;
  }
}

type PublishFormData = {
  is_featured: boolean;
  is_hidden: boolean;
  release_notes: string;
  tags: string[];
  etag?: string;
  details_url?: string;
};

type PublishAgentSidebarProps = {
  open: boolean;
  agent: UnifiedAgent | null;
  onClose: () => void;
  onPublish: (data: PublishFormData) => void;
  isPublishing: boolean;
};

export function PublishAgentSidebar({
  open,
  agent,
  onClose,
  onPublish,
  isPublishing,
}: PublishAgentSidebarProps) {
  const [formState, dispatch] = useReducer(formReducer, initialFormState);

  // Fetch existing agent details from directory if available
  const { data: agentDetails } = useAgentDirectoryDetails(agent?.agent_id || null);

  // Hydrate form with existing agent directory data
  useEffect(() => {
    if (agentDetails) {
      dispatch({ type: 'HYDRATE_FORM', payload: agentDetails });
    }
  }, [agentDetails]);

  const handleSubmit = () => {
    // Convert comma-separated tags to array
    const tagsArray = formState.tagsInput
      .split(',')
      .map((tag: string) => tag.trim())
      .filter((tag: string) => tag.length > 0);

    onPublish({
      is_featured: formState.is_featured,
      is_hidden: formState.is_hidden,
      release_notes: formState.release_notes,
      tags: tagsArray,
      etag: formState.etag || undefined,
      details_url: formState.details_url || undefined,
    });
  };

  const handleClose = () => {
    dispatch({ type: 'RESET_FORM' });
    onClose();
  };

  return (
    <Sidebar open={open} dock="right" initialWidth={400} minWidth={350} maxWidth={600}>
      <Flex flexDirection="column" gap="16" p="24">
        <Flex flexDirection="row" justifyContent="space-between" alignItems="center">
          <Text fontWeight="600" fontSize="lg">
            Publish Agent
          </Text>
          <Button icon={<IconXmark size="16" />} appearance="default" onClick={handleClose} />
        </Flex>

        {agent && (
          <Flex flexDirection="column" gap="16">
            <Field label="Agent">
              <Text fontSize="sm" color="fg.secondary">
                {agent.name}
              </Text>
            </Field>

            <Field label="ETag" info="Version identifier for this published agent">
              <Input
                value={formState.etag}
                readOnly
                placeholder="ETag will appear here for published agents"
              />
            </Field>

            <Field
              label="Details URL"
              info="Optional URL with additional information about this agent"
            >
              <Input
                value={formState.details_url}
                onChange={e =>
                  dispatch({ type: 'UPDATE_FIELD', field: 'details_url', value: e.target.value })
                }
                placeholder="https://example.com/agent-documentation"
              />
            </Field>

            <Field label="Visibility Options">
              <Flex flexDirection="column" gap="12">
                <Switch
                  checked={formState.is_featured}
                  onCheckedChange={value =>
                    dispatch({ type: 'UPDATE_FIELD', field: 'is_featured', value })
                  }
                >
                  Featured
                </Switch>
                <Text fontSize="sm" color="fg.tertiary">
                  Mark this agent as featured in the directory
                </Text>

                <Switch
                  checked={formState.is_hidden}
                  onCheckedChange={value =>
                    dispatch({ type: 'UPDATE_FIELD', field: 'is_hidden', value })
                  }
                >
                  Hidden
                </Switch>
                <Text fontSize="sm" color="fg.tertiary">
                  Hide this agent from public browse
                </Text>
              </Flex>
            </Field>

            <Field label="Tags">
              <Input
                value={formState.tagsInput}
                onChange={e =>
                  dispatch({ type: 'UPDATE_FIELD', field: 'tagsInput', value: e.target.value })
                }
                placeholder="marketing, automation, content"
              />
              <Text fontSize="sm" color="fg.tertiary">
                Enter tags separated by commas
              </Text>
            </Field>

            <Field label="Release Notes">
              <Textarea
                value={formState.release_notes}
                onChange={e =>
                  dispatch({ type: 'UPDATE_FIELD', field: 'release_notes', value: e.target.value })
                }
                placeholder="Describe what's new or changed in this version..."
                resize="vertical"
                rows={4}
              />
            </Field>

            <Button onClick={handleSubmit} disabled={isPublishing} appearance="primary">
              {isPublishing ? 'Publishing...' : 'Publish Agent'}
            </Button>
          </Flex>
        )}
      </Flex>
    </Sidebar>
  );
}
